import { Component, type ComponentInterface, Element, Event, type EventEmitter, Method, Prop, Watch, h } from '@stencil/core';
import type { InputMask } from 'imask';
import IMask from 'imask'; // It is supposed to be modular, but we import everything for the sake of universal use.
import { type Attributes, inheritAriaAttributes, inheritAttributes } from '~/util/DOM';

/**
 * @slot iconLeft - Slot for an icon to be placed on the left side of the input.
 * @slot iconRight - Slot for an icon to be placed on the right side of the input.
 */
@Component({
  tag: 'ltc-input-text',
  styleUrl: 'input-text.less',
  scoped: true,
})
export class LtcInputText implements ComponentInterface {
  /**
   * 1. Own Properties
   */
  public static counter = 0;

  private hasLeftIconSlot = false;
  private hasRightIconSlot = false;
  private inheritedAttributes: Attributes = {};
  private inputEl?: HTMLInputElement;

  private mask?: InputMask;

  /**
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLLtcInputTextElement;

  /**
   * 3. State() variables
   */

  /**
   * 4. Public Property API
   */
  /**
   * The type of autocomplete functionality enabled on the input.
   */
  @Prop() autocomplete: string;
  /**
   * Identifier of the focus input to match a label defined for the component.
   */
  @Prop() inputId = `ltc-input-text-${LtcInputText.counter++}`;

  /**
   * Text input label
   */
  @Prop() label: string;

  /**
   * Value of the input text
   */
  @Prop({ mutable: true }) value: string;

  /**
   * Value of the input placeholder text
   */
  @Prop() placeholder: string;

  /**
   * Name of the input text.
   */
  @Prop() name = this.inputId;

  /**
   * When present, it specifies that the element should be disabled
   */
  @Prop() disabled = false;

  /**
   * When present, it specifies that the element value cannot be changed
   */
  @Prop() readonly = false;
  /**
   * When present, it specifies that an input field is required.
   */
  @Prop() required = false;

  /**
   * Optional property for passing input mask options
   */
  @Prop() iMaskOptions: Record<string, unknown>;

  /**
   * Prop lifecycle events
   */

  @Watch('value')
  valueChanged(newValue: string) {
    if (this.inputEl) {
      if (this.mask) {
        this.mask.rawInputValue = newValue;
        this.mask.updateValue();
        this.mask.updateControl();
        // only update if model and view differ
      } else if (this.inputEl.value !== this.value) {
        this.inputEl.value = this.value;
      }
    }
  }

  /**
   * 5. Events section
   */
  /**
   * Fired when value of input text changes
   */
  @Event({ composed: true, bubbles: true }) ltcChange: EventEmitter<string>;

  /**
   * 6. Component lifecycle events
   */

  componentWillLoad() {
    this.hasLeftIconSlot = !!this.host.querySelector('[slot="leftIcon"]');
    this.hasRightIconSlot = !!this.host.querySelector('[slot="rightIcon"]');

    this.inheritedAttributes = {
      ...inheritAriaAttributes(this.host),
      ...inheritAttributes(this.host, ['inputmode']),
    };
  }

  connectedCallback(): void {
    this.createMask();
  }

  disconnectedCallback(): void {
    this.destroyMask();
  }

  /**
   * 7. Listeners
   */

  /**
   * 8. Public methods API
   */
  /**
   * Retrieves a reference to the input element within the component.
   *
   * @returns {HTMLInputElement | undefined} The input element, or undefined if not found.
   * @example
   * const inputElement = await myComponent.getInputRef();
   * if (inputElement) {
   *   // Do something with the input element
   *   inputElement.focus();
   * }
   */
  @Method()
  async getInputRef(): Promise<HTMLInputElement | undefined> {
    return this.inputEl;
  }

  /**
   * Force-sets the values on the element. Use this method in ltcChange listener
   * to refresh mask for unchanged value in controlled component.
   * @param value the value to set
   */
  @Method()
  async setValue(value: string): Promise<void> {
    this.value = value ?? '';
  }

  /**
   * 9. Local methods
   */
  private inputChanged = (e: InputEvent) => {
    const value = (e.target as HTMLInputElement)?.value;
    if (this.value !== value) {
      this.value = value || '';
      this.ltcChange.emit(this.value);
    }
  };

  private inputRefSetter = (el?: HTMLInputElement) => {
    this.inputEl = el;
    this.createMask();
  };

  private onFocusin = (e: FocusEvent) => {
    (e.target as HTMLInputElement)?.select();
  };

  private onMaskChange = () => {
    if (this.mask) {
      const value = this.mask.unmaskedValue;

      if (this.value !== value) {
        this.value = value || '';
        this.ltcChange.emit(this.value);
      }

      this.mask.updateValue();
      this.mask.updateControl();
    }
  };

  createMask() {
    this.destroyMask();

    if (this.iMaskOptions && this.inputEl) {
      this.mask = IMask(this.inputEl, this.iMaskOptions);
      this.mask.rawInputValue = this.value || '';
      this.mask.updateValue();
      this.mask.updateControl();
    }
  }

  destroyMask() {
    if (this.mask) {
      this.mask.destroy();
    }
  }

  /**
   * 10. render() function
   */
  render() {
    const {
      autocomplete,
      disabled,
      hasLeftIconSlot,
      hasRightIconSlot,
      iMaskOptions,
      inheritedAttributes,
      inputId,
      label,
      name,
      placeholder,
      readonly,
      required,
      value,
    } = this;

    const controlledValue = iMaskOptions
      ? {
          onFocusout: this.onMaskChange,
          onKeyUp: (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
              this.onMaskChange();
            }
          },
        }
      : { value, onInput: this.inputChanged };

    return (
      <div class={{ 'input-container': true, 'ltc-input-filled': !!value, 'ltc-input-labelled': !!label }}>
        <slot name="leftIcon" />
        <input
          ref={this.inputRefSetter}
          type="text"
          id={inputId}
          autocomplete={autocomplete}
          name={name}
          placeholder={label ? '' : placeholder}
          disabled={disabled}
          readonly={readonly}
          required={required}
          aria-disabled={String(disabled)}
          aria-readonly={String(readonly)}
          aria-required={String(required)}
          onFocusin={this.onFocusin}
          {...controlledValue}
          {...inheritedAttributes}
        />
        <slot name="rightIcon" />

        {label ? (
          <label
            htmlFor={inputId}
            class={{
              'input-label': true,
              'left-icon-shift': hasLeftIconSlot,
              'right-icon-shift': hasRightIconSlot,
            }}
          >
            {label}
          </label>
        ) : null}
      </div>
    );
  }
}
